import React from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import CustomEmptyState from "global/components/CustomEmptyState/CustomEmptyState";

type PieItem = {
  name: string;
  value: number;
};

interface Props {
  data: PieItem[];
  selectedMetrics: string[];
}

const RequestStatusPieChart = ({ data, selectedMetrics }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { status } = theme.palette.chart;

  // Map an API status name (e.g. "In Review", "InReview") to a palette token.
  const colorForStatus = (name: string): string => {
    const key = String(name)
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    const map: Record<string, string> = {
      pending: status.pending,
      signed: status.signed,
      completed: status.signed,
      scheduled: status.scheduled,
      draft: status.draft,
      declined: status.declined,
      rejected: status.rejected,
      inreview: status.inReview,
      voided: status.voided,
      expired: status.expired,
    };
    return map[key] || status.fallback;
  };

  const chartData = data
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  const isAllUnchecked = selectedMetrics.length === 0;
  const hasData = chartData.length > 0;

  if (isAllUnchecked || !hasData) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: isMobile ? 330 : 390,
        }}
      >
        <CustomEmptyState
          icon="report"
          title={isAllUnchecked ? "No status selected" : "No data available"}
          description={
            isAllUnchecked
              ? "Please select at least one status."
              : "No data available for the selected date range."
          }
          isMobile={isMobile}
        />
      </Box>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const percentOfTotal = (value: number) =>
    total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

  /**
   * The donut is one graphic to assistive tech (`role="img"` below). The list
   * beside it already gives every name and count in text, so the label only has
   * to add what the slices alone convey — the proportions (WCAG 1.1.1).
   */
  const chartSummary =
    `Donut chart. ${total.toLocaleString()} requests by status: ` +
    chartData
      .map(
        (item) =>
          `${item.name} ${item.value.toLocaleString()}, ` +
          `${percentOfTotal(item.value)} percent`,
      )
      .join("; ") +
    ".";

  const donutSize = isMobile ? 200 : 230;
  const outerRadius = isMobile ? 92 : 105;
  const innerRadius = isMobile ? 60 : 70;

  return (
    <Stack
      direction={isMobile ? "column" : "row"}
      alignItems="center"
      justifyContent="center"
      spacing={isMobile ? 2 : 4}
      sx={{ width: "100%", minHeight: isMobile ? 330 : 390 }}
    >
      <Box
        role="img"
        aria-label={chartSummary}
        sx={{
          position: "relative",
          width: donutSize,
          height: donutSize,
          flexShrink: 0,
          // Scoped to the plot: a blanket `& :focus` also erased the focus ring
          // of anything else that ever landed in this box (WCAG 2.4.7).
          "& .recharts-wrapper :focus, & .recharts-wrapper:focus": {
            outline: "none",
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart accessibilityLayer={false}>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              cornerRadius={3}
              startAngle={90}
              endAngle={-270}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
              isAnimationActive={false}
              labelLine={false}
            >
              {chartData.map((entry, idx) => (
                <Cell
                  key={`status-cell-${idx}`}
                  fill={colorForStatus(entry.name)}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: any, name: any) => [
                Number(value) || 0,
                String(name),
              ]}
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: theme.palette.chart.tooltipShadow,
              }}
            />
          </PieChart>
        </ResponsiveContainer>

        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={0.25}
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}
            color="text.primary"
          >
            {total.toLocaleString()}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
            }}
            color="text.secondary"
          >
            REQUESTS
          </Typography>
        </Stack>
      </Box>

      {/* A list of statuses, so it is exposed as one — with the browser's own
          list margins zeroed out so nothing moves (WCAG 1.3.1). */}
      <Stack
        component="ul"
        aria-label="Requests by status"
        spacing={1.25}
        sx={{
          minWidth: 0,
          width: isMobile ? "100%" : 240,
          maxWidth: isMobile ? 320 : 240,
          listStyle: "none",
          m: 0,
          p: 0,
        }}
      >
        {chartData.map((item) => (
          <Stack
            component="li"
            key={item.name}
            direction="row"
            alignItems="center"
            spacing={1.25}
          >
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: "3px",
                flexShrink: 0,
                bgcolor: colorForStatus(item.name),
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                flex: 1,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ fontWeight: 800 }}
            >
              {item.value.toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default RequestStatusPieChart;
